import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import OutsideClickHandler from "react-outside-click-handler";
import { observer } from "mobx-react";
import { CommandCentreVenue } from "../state/UserState";

export const NAV_COLORS = {
  headerBg: "#17171A",
  navBg: "#0F0F11",
  divider: "rgba(255, 255, 255, 0.08)",
  // Brand accent from @playerelite/ui (all-brands.css): gold for PE/USA, blue for Nexus.
  accent: "rgb(var(--pe-brand-400))",
  textPrimary: "#FFFFFF",
  textMuted: "#9CA3AF",
  navIdle: "#D1D5DB",
  navHover: "rgba(255, 255, 255, 0.06)",
  navActive: "#26262B",
  panelBg: "#FFFFFF",
  panelHover: "#F3F4F6",
  panelTitle: "#111827",
  panelDesc: "#6B7280",
  panelDivider: "#EFEFEF",
};

type VenueSwitcherProps = {
  isSingleVenue: boolean;
  selectedVenue?: CommandCentreVenue;
  userVenues?: CommandCentreVenue[];
  totalVenueCount: number;
  selectVenue: (venue: CommandCentreVenue) => void;
  venueSearchTerm?: string;
  updateVenueSearchTerm: (venueSearchTerm: string) => void;
  variant?: "header" | "drawer";
};

const venueLabel = (venue?: CommandCentreVenue) =>
  venue ? `Venue ${venue.venueId} — ${venue.displayName}` : "";

const VenueSwitcher = observer((props: VenueSwitcherProps) => {
  const { variant = "header" } = props;
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => searchBoxRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const itemSelected = (venue: CommandCentreVenue) => {
    setIsOpen(false);
    props.selectVenue(venue);
  };

  const label = venueLabel(props.selectedVenue);

  // A single-venue user has nothing to switch to — render the label only.
  if (props.isSingleVenue) {
    return (
      <Typography
        noWrap
        sx={{
          color: NAV_COLORS.accent,
          fontSize: variant === "drawer" ? "0.8125rem" : "0.75rem",
          fontWeight: 500,
          letterSpacing: "0.01em",
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
    );
  }

  return (
    <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
      <Box sx={{ position: "relative" }}>
        <Box
          component="button"
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            p: 0,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontFamily: "inherit",
            color: NAV_COLORS.accent,
            fontSize: variant === "drawer" ? "0.8125rem" : "0.75rem",
            fontWeight: 500,
            letterSpacing: "0.01em",
            lineHeight: 1.4,
            maxWidth: "100%",
            "&:hover": { color: "#E3C766" },
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Box>
          <KeyboardArrowDown
            sx={{
              fontSize: 15,
              flexShrink: 0,
              transition: "transform 0.18s ease",
              transform: isOpen ? "rotate(180deg)" : "none",
            }}
          />
        </Box>

        {isOpen && (
          <Box
            role="listbox"
            sx={{
              position: "absolute",
              top: "calc(100% + 10px)",
              left: variant === "drawer" ? 0 : -8,
              zIndex: 1500,
              width: 300,
              backgroundColor: NAV_COLORS.panelBg,
              borderLeft: `3px solid ${NAV_COLORS.accent}`,
              boxShadow: "0 14px 32px rgba(0, 0, 0, 0.32)",
            }}
          >
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${NAV_COLORS.panelDivider}` }}>
              <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
                <SearchIcon
                  sx={{
                    position: "absolute",
                    left: 10,
                    fontSize: 18,
                    color: NAV_COLORS.panelDesc,
                    pointerEvents: "none",
                  }}
                />
                <Box
                  component="input"
                  ref={searchBoxRef}
                  type="text"
                  value={props.venueSearchTerm ?? ""}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    props.updateVenueSearchTerm(event.target.value.toLowerCase())
                  }
                  placeholder="Search venue"
                  sx={{
                    width: "100%",
                    py: 1,
                    pl: 4.5,
                    pr: 1.5,
                    fontFamily: "inherit",
                    fontSize: "0.8125rem",
                    color: NAV_COLORS.panelTitle,
                    backgroundColor: "#F9FAFB",
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px",
                    outline: "none",
                    "&:focus": { borderColor: NAV_COLORS.accent },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
              {props.userVenues?.length ? (
                props.userVenues.map((venue) => {
                  const isSelected = venue.venueId === props.selectedVenue?.venueId;
                  return (
                    <Box
                      key={venue.venueId}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => itemSelected(venue)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        cursor: "pointer",
                        backgroundColor: isSelected ? NAV_COLORS.panelHover : "transparent",
                        "&:hover": { backgroundColor: NAV_COLORS.panelHover },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          flexShrink: 0,
                          borderRadius: "4px",
                          backgroundColor: "#E5E7EB",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={venue.iconUrl}
                          alt=""
                          loading="lazy"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: isSelected ? 600 : 500,
                            color: NAV_COLORS.panelTitle,
                          }}
                        >
                          {venue.displayName}
                        </Typography>
                        <Typography sx={{ fontSize: "0.6875rem", color: NAV_COLORS.panelDesc }}>
                          Venue {venue.venueId}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Typography
                  sx={{ px: 2, py: 2, fontSize: "0.8125rem", color: NAV_COLORS.panelDesc }}
                >
                  No venues match that search.
                </Typography>
              )}
            </Box>

            <Typography
              sx={{
                px: 2,
                py: 1,
                fontSize: "0.6875rem",
                color: NAV_COLORS.panelDesc,
                borderTop: `1px solid ${NAV_COLORS.panelDivider}`,
              }}
            >
              {props.userVenues?.length ?? 0} of {props.totalVenueCount} venues
            </Typography>
          </Box>
        )}
      </Box>
    </OutsideClickHandler>
  );
});

export default VenueSwitcher;
